import React, { createContext, useContext, useState, ReactNode } from "react";
import { Snackbar, Alert as MuiAlert, AlertColor } from "@mui/material";

// Define the shape of the alert context
export interface AlertContextType {
  setAlert: (status: AlertColor, message: string) => void;
}

// Create the context
export const AlertContext = createContext<AlertContextType | undefined>(
  undefined
);

// Hook to use the alert context
export const useAlert = (): AlertContextType => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error("useAlert must be used within an AlertProvider");
  }
  return context;
};

// AlertProvider that will wrap the application
export const AlertProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [open, setOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState<{
    status: AlertColor;
    message: string;
  }>({
    status: "success",
    message: "",
  });

  // Function to trigger an alert
  const setAlert = (status: AlertColor, message: string) => {
    setAlertConfig({ status, message });
    setOpen(true);
  };

  // Close the alert automatically after a set duration
  const handleClose = () => {
    setOpen(false);
  };

  return (
    <AlertContext.Provider value={{ setAlert }}>
      {children}

      {/* Snackbar for showing alerts */}
      <Snackbar
        open={open}
        autoHideDuration={4000} // Duration before the alert auto-hides
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }} // Fixed position of the alert
      >
        <MuiAlert
          onClose={handleClose}
          severity={alertConfig.status}
          sx={{ width: "100%" }}
        >
          {alertConfig.message}
        </MuiAlert>
      </Snackbar>
    </AlertContext.Provider>
  );
};
